import { createContext, useContext, useEffect, useMemo, useState } from "react";
import products from "../data/products.json";

const CartContext = createContext(null);
const STORAGE_KEY = "strangelot-cart";

const lineKey = (id, size) => `${id}::${size || ""}`;

const loadCart = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [lines, setLines] = useState(loadCart);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  }, [lines]);

  const addItem = (productId, size, qty = 1) => {
    setLines((prev) => {
      const key = lineKey(productId, size);
      const existing = prev.find((l) => lineKey(l.productId, l.size) === key);
      if (existing) {
        return prev.map((l) =>
          lineKey(l.productId, l.size) === key ? { ...l, qty: l.qty + qty } : l
        );
      }
      return [...prev, { productId, size, qty }];
    });
    setIsOpen(true);
  };

  const removeItem = (productId, size) => {
    const key = lineKey(productId, size);
    setLines((prev) => prev.filter((l) => lineKey(l.productId, l.size) !== key));
  };

  const updateQty = (productId, size, qty) => {
    if (qty < 1) return removeItem(productId, size);
    const key = lineKey(productId, size);
    setLines((prev) =>
      prev.map((l) => (lineKey(l.productId, l.size) === key ? { ...l, qty } : l))
    );
  };

  const clearCart = () => setLines([]);

  const items = useMemo(
    () =>
      lines
        .map((l) => {
          const product = products.find((p) => p.id === l.productId);
          if (!product) return null;
          return { ...l, product };
        })
        .filter(Boolean),
    [lines]
  );

  const itemCount = items.reduce((sum, i) => sum + i.qty, 0);
  const subtotalUSD = items.reduce(
    (sum, i) => sum + i.product.priceUSD * i.qty,
    0
  );

  const value = {
    items,
    itemCount,
    subtotalUSD,
    addItem,
    removeItem,
    updateQty,
    clearCart,
    isOpen,
    setIsOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
};
